import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSpreadsheet, FileText, Table2, LoaderCircle } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';

/**
 * Xuất MỘT chứng từ: CSV, PDF, và Excel ở nơi có bộ dựng workbook.
 *
 * Trước đây chỉ hoá đơn chiếu lệ có hộp thoại này; mọi chứng từ khác có một nút
 * Xuất tải thẳng CSV, không hỏi gì. Người dùng nhìn nút đó rồi kết luận hệ
 * thống không xuất được PDF — trong khi bản in vẫn lưu PDF được từ lâu. Chức
 * năng có mà nằm sai chỗ thì với người dùng là không có.
 *
 * PDF KHÔNG dựng bằng thư viện: mẫu in đã là HTML đúng bố cục, trình duyệt
 * "Save as PDF" cho ra đúng tệp đó, còn một thư viện PDF thì cộng vài trăm KB
 * vào mỗi lần tải trang của mọi người. Nên `onPdf` chỉ mở bản xem trước.
 *
 * `onExcel` là TUỲ CHỌN: không truyền thì không hiện lựa chọn Excel, thay vì
 * hiện một nút bấm vào không ra gì.
 */
export interface DocumentExportDialogProps {
  /** Số chứng từ, hiện trên tiêu đề. */
  documentNo: string;
  onCsv: () => void;
  onPdf: () => void;
  onExcel?: () => Promise<{ logoMissing: boolean }>;
  onClose: () => void;
}

export function DocumentExportDialog({ documentNo, onCsv, onPdf, onExcel, onClose }: DocumentExportDialogProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState('');
  // Đang dựng workbook thì KHÔNG cho đóng: đóng giữa chừng là người dùng mất
  // tệp mà không hiểu vì sao, còn tiến trình vẫn chạy nốt vô ích.
  const locked = useRef(false);

  const excel = async () => {
    if (!onExcel || locked.current) return;
    locked.current = true; setBusy(true); setError(false); setStatus('');
    try {
      const result = await onExcel();
      setStatus(result.logoMissing ? 'docExport.logoMissing' : 'docExport.downloaded');
    } catch { setError(true); } finally { setBusy(false); locked.current = false; }
  };

  const optionClass = 'flex w-full items-start gap-3 rounded border border-line p-4 text-left hover:border-brand hover:bg-brand-tint focus-visible:outline-2 focus-visible:outline-brand disabled:opacity-50';
  return <Modal title={`${t('dataExport.title')} · ${documentNo}`} closeLabel={t('common.close')} onClose={() => { if (!locked.current) onClose(); }} className="max-w-lg">
    <div className="space-y-3 overflow-y-auto p-5" aria-busy={busy}>
      <button className={optionClass} disabled={busy} onClick={() => { setError(false); try { onCsv(); onClose(); } catch { setError(true); } }}>
        <Table2 className="mt-0.5 size-5 shrink-0 text-brand" aria-hidden="true" />
        <span><strong className="block text-sm">{t('docExport.csv')}</strong><span className="mt-1 block text-xs text-ink-soft">{t('docExport.csvHint')}</span></span>
      </button>
      <button className={optionClass} disabled={busy} onClick={onPdf}>
        <FileText className="mt-0.5 size-5 shrink-0 text-brand" aria-hidden="true" />
        <span><strong className="block text-sm">{t('docExport.pdf')}</strong><span className="mt-1 block text-xs text-ink-soft">{t('docExport.pdfHint')}</span></span>
      </button>
      {onExcel && <button className={optionClass} disabled={busy} onClick={() => void excel()}>
        {busy ? <LoaderCircle className="mt-0.5 size-5 shrink-0 animate-spin text-brand" aria-hidden="true" /> : <FileSpreadsheet className="mt-0.5 size-5 shrink-0 text-brand" aria-hidden="true" />}
        <span><strong className="block text-sm">{t(busy ? 'docExport.preparing' : 'docExport.excel')}</strong><span className="mt-1 block text-xs text-ink-soft">{t('docExport.excelHint')}</span></span>
      </button>}
      {error && <p role="alert" className="text-sm text-danger">{t('docExport.failed')}</p>}
      {status && <p role="status" className="text-sm text-ink-soft">{t(status)}</p>}
    </div>
  </Modal>;
}
